import { useEffect } from 'react'

interface FontConfig {
  href: string
  selector: string
}

/**
 * Custom hook to dynamically load Google Fonts
 * @param fontConfigs Array of font configurations with href and selector
 * @param cleanupOnUnmount Whether to remove the font links when component unmounts (default: false)
 */
export const useGoogleFonts = (fontConfigs: FontConfig[], cleanupOnUnmount: boolean = false) => {
  useEffect(() => {
    // Check if any of our specific fonts are already loaded
    const fontsToLoad = fontConfigs.filter((config) => {
      const fontLoaded = document.querySelector(config.selector)
      return !fontLoaded
    })

    // Only load if we have fonts that need loading
    if (fontsToLoad.length > 0) {
      const links: HTMLLinkElement[] = []

      // Create and append link elements for each font that needs loading
      fontsToLoad.forEach((config) => {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = config.href

        // Append to head
        document.head.appendChild(link)
        links.push(link)
      })

      // Cleanup function to remove the links when component unmounts (optional)
      if (cleanupOnUnmount) {
        return () => {
          links.forEach((link) => {
            if (document.head.contains(link)) {
              document.head.removeChild(link)
            }
          })
        }
      }
    }
  }, [fontConfigs, cleanupOnUnmount])
}

/**
 * Predefined font configurations for common Google Fonts
 */
export const GOOGLE_FONTS = {
  EB_GARAMOND: {
    href: 'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400..800;1,400..800&display=swap',
    selector: 'link[href*="EB+Garamond"]',
  },
  MONTSERRAT: {
    href: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300..700;1,300..700&display=swap',
    selector: 'link[href*="Montserrat"]',
  },
  INTER: {
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300..700;1,300..700&display=swap',
    selector: 'link[href*="Inter"]',
  },
  LATO: {
    href: 'https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400;1,600;1,700&display=swap',
    selector: 'link[href*="Lato"]',
  },
  MAITREE: {
    href: 'https://fonts.googleapis.com/css2?family=Maitree:wght@200;300;400;500;600;700&display=swap',
    selector: 'link[href*="Maitree"]',
  },
  NUNITO_SANS: {
    href: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,200..900;1,200..900&display=swap',
    selector: 'link[href*="Nunito+Sans"]',
  },
  LIBRE_BASKERVILLE: {
    href: 'https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&display=swap',
    selector: 'link[href*="Libre+Baskerville"]',
  },
  PLAYFAIR_DISPLAY: {
    href: 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap',
    selector: 'link[href*="Playfair+Display"]',
  },
} as const
